import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import {
    Drawer,
    Row,
    Col,
    Typography,
    Tag,
    Card,
    Alert,
    Empty,
    Divider,
    Button,
    Space,
    Tooltip,
} from 'antd';
import { EditOutlined, SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import HtmlParser from 'react-html-parser';
import {
    isValidDate,
    renderMoney,
    renderCustomerServiceStatus,
    renderServiceGroup,
} from '@/utils/helpers';
import CreatedAtCell from '../CreatedAtCell';
import CustomerServiceStaffHistory from './CustomerServiceStaffHistory';
import CustomerServiceFinancialHistory from './CustomerServiceFinancialHistory';
import CustomerServiceFailureReportForm from './CustomerServiceFailureReportForm';
import { getCustomerServiceDoctorRoleGroups } from './customerServiceDoctorRoleHelpers';
import { openCommissionActionModal, openCommissionRecalculationResult } from '@/utils/commission/actionModal';

const { Paragraph } = Typography;

class CustomerServicesDetail extends Component {
    renderDoctorRoleGroup = (group) => this.renderStaffGroup(
        group.label,
        group.people.map(person => ({ doctor: { id: person.id, full_name: person.name } })),
        'doctor'
    );
    renderInfoItem = (label, children, options = {}) => {
        const { strong = false } = options;

        return (
            <div
                style={{
                    minHeight: 54,
                    padding: '6px 0',
                }}
            >
                <div
                    style={{
                        marginBottom: 2,
                        fontSize: 13,
                        lineHeight: '20px',
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                >
                    {label}
                </div>

                <div
                    style={{
                        fontSize: strong ? 17 : 16,
                        lineHeight: '23px',
                        fontWeight: strong ? 700 : 600,
                        color: 'rgba(0, 0, 0, 0.88)',
                        wordBreak: 'break-word',
                    }}
                >
                    {children}
                </div>
            </div>
        );
    };

    renderSectionTitle = (title, description = null, extra = null) => (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 10,
            }}
        >
            <div>
                <div
                    style={{
                        fontSize: 18,
                        lineHeight: '24px',
                        fontWeight: 700,
                        color: 'rgba(0, 0, 0, 0.88)',
                    }}
                >
                    {title}
                </div>

                {description && (
                    <div
                        style={{
                            marginTop: 2,
                            fontSize: 13,
                            lineHeight: '20px',
                            color: 'rgba(0, 0, 0, 0.45)',
                        }}
                    >
                        {description}
                    </div>
                )}
            </div>

            {extra}
        </div>
    );

    renderCostMetric = (label, value, options = {}) => {
        const { primary = false } = options;

        return (
            <div
                style={{
                    height: '100%',
                    minHeight: 84,
                    padding: '12px 14px',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    background: primary ? '#f0f7ff' : '#fafafa',
                }}
            >
                <div
                    style={{
                        marginBottom: 5,
                        fontSize: 13,
                        lineHeight: '20px',
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                >
                    {label}
                </div>
                <div
                    style={{
                        fontSize: primary ? 20 : 17,
                        lineHeight: primary ? '28px' : '24px',
                        fontWeight: primary ? 700 : 600,
                        color: primary ? '#1677ff' : 'rgba(0, 0, 0, 0.88)',
                        wordBreak: 'break-word',
                    }}
                >
                    {value}
                </div>
            </div>
        );
    };

    renderStaffGroup = (title, list = [], type = 'sales') => {
        const mapConfig = {
            sales: { userKey: 'sales', idKey: 'sales_id' },
            doctor: { userKey: 'doctor', idKey: 'doctor_id' },
            assistant: { userKey: 'assistant', idKey: 'assistant_id' },
        };

        const config = mapConfig[type];

        return (
            <div
                style={{
                    height: '100%',
                    padding: '10px 12px',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    background: '#fff',
                }}
            >
                <div
                    style={{
                        marginBottom: 6,
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 700,
                        color: 'rgba(0, 0, 0, 0.65)',
                    }}
                >
                    {title}
                </div>

                {Array.isArray(list) && list.length > 0 ? (
                    <div
                        style={{
                            display: 'grid',
                            gap: 5,
                        }}
                    >
                        {list.map((item, index) => {
                            const user = item?.[config.userKey];
                            const roleName = user?.user_role?.name;
                            const fullName = user?.full_name;
                            const isImplantDoctor =
                                type === 'doctor' &&
                                Number(item?.is_implant_doctor) === 1;

                            return (
                                <div
                                    key={
                                        item?.id ||
                                        item?.[config.idKey] ||
                                        index
                                    }
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 6,
                                        padding: '5px 0',
                                        borderBottom:
                                            index < list.length - 1
                                                ? '1px solid #f5f5f5'
                                                : 'none',
                                    }}
                                >
                                    <div
                                        style={{
                                            minWidth: 0,
                                            fontSize: 15,
                                            lineHeight: '22px',
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        >
                                            {roleName || 'Nhân sự'}:
                                        </span>{' '}
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                color: 'rgba(0, 0, 0, 0.88)',
                                            }}
                                        >
                                            {fullName || '-'}
                                        </span>
                                    </div>

                                    {isImplantDoctor && (
                                        <Tag
                                            color="blue"
                                            style={{
                                                margin: 0,
                                                fontSize: 13,
                                            }}
                                        >
                                            Bác sĩ cắm trụ
                                        </Tag>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div
                        style={{
                            padding: '5px 0',
                            fontSize: 14,
                            lineHeight: '20px',
                            color: 'rgba(0, 0, 0, 0.35)',
                        }}
                    >
                        Chưa có dữ liệu
                    </div>
                )}
            </div>
        );
    };

    render() {
        const {
            visible,
            currentItem,
            onCancel,
            staffRoles,
            staffComponents,
            staffChangeReasons,
            doctors,
            onStaffChanged,
            allowCommissionSetup,
            onCommissionSetup,
            allowCommissionRecalculate,
            onCommissionRecalculate,
            allowEdit,
            onEdit,
            zIndex,
        } = this.props;

        const isTransferred = !!currentItem?.is_transferred;
        const customer = currentItem?.customer || {};

        const serviceName =
            currentItem?.service_name ||
            currentItem?.service?.name ||
            currentItem?.items?.[0]?.name ||
            '-';

        const serviceGroup = currentItem?.service?.service_group || '-';
        const serviceCode = currentItem?.service?.code || null;
        const customerSource = customer?.customer_source?.name || '-';

        const doctorRoleGroups = getCustomerServiceDoctorRoleGroups(currentItem, { includeEmpty: true });

        const pageCardStyle = {
            marginBottom: 12,
            borderRadius: 10,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        };

        return (
            <Drawer
                visible={visible}
                title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span>Chi tiết dịch vụ</span>
                    <Space>
                        {allowEdit ? (
                            <Tooltip placement="left" title={isTransferred ? 'Không thể sửa vì đã chuyển sang dịch vụ khác' : 'Sửa dịch vụ hiện tại'}>
                                <Button
                                    type="dashed"
                                    icon={<EditOutlined />}
                                    onClick={onEdit}
                                    disabled={isTransferred}
                                >
                                    Sửa dịch vụ
                                </Button>
                            </Tooltip>
                        ) : null}
                        {allowCommissionSetup ? (
                            <Tooltip title="Mở thiết lập hoa hồng của dịch vụ" placement="left">
                                <Button type="primary" icon={<SettingOutlined />} onClick={onCommissionSetup}>Thiết lập hoa hồng</Button>
                            </Tooltip>
                        ) : null}
                        {allowCommissionRecalculate ? (
                            <Tooltip title="Tính lại hoa hồng của riêng dịch vụ này" placement="left">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => openCommissionActionModal({
                                        action: 'rebuild',
                                        onOk: async () => {
                                            const result = await onCommissionRecalculate(currentItem.id);
                                            openCommissionRecalculationResult(result);
                                        },
                                    })}
                                >Tính lại hoa hồng</Button>
                            </Tooltip>
                        ) : null}
                    </Space>
                </div>}
                onClose={onCancel}
                maskClosable={false}
                width={isMobile ? '100%' : '70%'}
                zIndex={zIndex}
                bodyStyle={{
                    background: '#f7f8fa',
                    padding: isMobile ? 10 : 14,
                }}
            >
                {visible && currentItem ? (
                    <>
                        {/* TỔNG QUAN DỊCH VỤ */}
                        <Card
                            bordered={false}
                            style={pageCardStyle}
                            bodyStyle={{
                                padding: isMobile ? 12 : '12px 16px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 10,
                                }}
                            >
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div
                                        style={{
                                            marginBottom: 3,
                                            fontSize: 13,
                                            lineHeight: '20px',
                                            color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                    >
                                        Nhóm dịch vụ:{' '}
                                        {renderServiceGroup(serviceGroup)}
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 6,
                                            marginBottom: 5,
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: isMobile ? 19 : 21,
                                                lineHeight: '28px',
                                                fontWeight: 700,
                                                color: '#1677ff',
                                            }}
                                        >
                                            {serviceName}
                                        </span>

                                        {serviceCode && (
                                            <Tag
                                                color="blue"
                                                style={{
                                                    margin: 0,
                                                    fontSize: 13,
                                                }}
                                            >
                                                {serviceCode}
                                            </Tag>
                                        )}

                                        <Tag
                                            color="green"
                                            style={{
                                                margin: 0,
                                                fontSize: 13,
                                            }}
                                        >
                                            Dịch vụ chốt
                                        </Tag>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 5,
                                        }}
                                    >
                                        {currentItem?.converted_from_service_name && (
                                            <Tag
                                                color="green"
                                                style={{ margin: 0 }}
                                            >
                                                Được chuyển từ:{' '}
                                                {
                                                    currentItem?.converted_from_service_name
                                                }
                                            </Tag>
                                        )}

                                        {isTransferred &&
                                            currentItem?.converted_to_service_name && (
                                                <Tag
                                                    color="red"
                                                    style={{ margin: 0 }}
                                                >
                                                    Đã chuyển sang:{' '}
                                                    {
                                                        currentItem?.converted_to_service_name
                                                    }
                                                </Tag>
                                            )}
                                        {currentItem?.service_conversion?.code && (
                                            <Tag color="purple" style={{ margin: 0 }}>
                                                Giao dịch chuyển đổi: {currentItem.service_conversion.code}
                                            </Tag>
                                        )}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        flexShrink: 0,
                                        textAlign: isMobile
                                            ? 'left'
                                            : 'right',
                                    }}
                                >
                                    {renderCustomerServiceStatus(
                                        currentItem?.status
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* THÔNG TIN CHUNG */}
                        <Card
                            bordered={false}
                            style={pageCardStyle}
                            bodyStyle={{
                                padding: isMobile ? 12 : '12px 16px',
                            }}
                        >
                            {this.renderSectionTitle(
                                'Thông tin chung',
                                'Thông tin khách hàng và thời điểm phát sinh dịch vụ'
                            )}

                            <Row gutter={[20, 0]}>
                                <Col xs={24} sm={12} lg={5}>
                                    {this.renderInfoItem(
                                        'Tên khách hàng',
                                        customer?.full_name || '-',
                                        { strong: true }
                                    )}
                                </Col>

                                <Col xs={24} sm={12} lg={4}>
                                    {this.renderInfoItem(
                                        'Mã khách hàng',
                                        customer?.code ? (
                                            <Paragraph
                                                style={{
                                                    margin: 0,
                                                    fontSize: 'inherit',
                                                    lineHeight: 'inherit',
                                                }}
                                                copyable={{
                                                    text: customer.code,
                                                    tooltips: false,
                                                }}
                                            >
                                                <Tag
                                                    color="#1890ff"
                                                    style={{
                                                        margin: 0,
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {customer.code}
                                                </Tag>
                                            </Paragraph>
                                        ) : (
                                            '-'
                                        )
                                    )}
                                </Col>

                                <Col xs={24} sm={12} lg={5}>
                                    {this.renderInfoItem(
                                        'Nguồn khách hàng',
                                        customerSource
                                    )}
                                </Col>

                                <Col xs={24} sm={12} lg={5}>
                                    {this.renderInfoItem(
                                        'Ngày chốt dịch vụ',
                                        currentItem?.selected_at
                                            ? isValidDate(
                                                currentItem.selected_at,
                                                false
                                            )
                                            : '-'
                                    )}
                                </Col>

                                <Col xs={24} lg={5}>
                                    {this.renderInfoItem(
                                        'Ngày tạo dịch vụ',
                                        <CreatedAtCell
                                            createdAt={
                                                currentItem?.created_at
                                            }
                                            user={currentItem?.created_user}
                                            layout="vertical"
                                        />
                                    )}
                                </Col>
                            </Row>

                            <Divider
                                style={{
                                    margin: '6px 0 10px',
                                }}
                            />

                            <Alert
                                message={
                                    <span
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Ghi chú
                                    </span>
                                }
                                description={
                                    <div
                                        style={{
                                            fontSize: 15,
                                            lineHeight: '22px',
                                        }}
                                    >
                                        {currentItem?.note
                                            ? HtmlParser(currentItem.note)
                                            : 'Không có ghi chú'}
                                    </div>
                                }
                                type="info"
                                className="fix-alert-element"
                                style={{
                                    borderRadius: 8,
                                    padding: '8px 12px',
                                }}
                            />
                        </Card>

                        {/* CHI PHÍ */}
                        <Card
                            bordered={false}
                            style={pageCardStyle}
                            bodyStyle={{
                                padding: isMobile ? 12 : '12px 16px',
                            }}
                        >
                            {this.renderSectionTitle(
                                'Chi phí',
                                'Giá niêm yết, giá bán, số lượng và thành tiền khách hàng',
                                <CustomerServiceFinancialHistory
                                    customerService={currentItem}
                                />
                            )}

                            <Row gutter={[10, 10]} align="stretch">
                                <Col xs={24} sm={12} xl={6}>
                                    {this.renderCostMetric(
                                        'Giá niêm yết / đơn vị',
                                        renderMoney(currentItem.regular_price)
                                    )}
                                </Col>
                                <Col xs={24} sm={12} xl={6}>
                                    {this.renderCostMetric(
                                        'Giá bán / đơn vị',
                                        renderMoney(currentItem.sale_price)
                                    )}
                                </Col>
                                <Col xs={24} sm={12} xl={4}>
                                    {this.renderCostMetric(
                                        'Số lượng',
                                        currentItem.quantity ?? 0
                                    )}
                                </Col>
                                <Col xs={24} sm={12} xl={8}>
                                    {this.renderCostMetric(
                                        'Thành tiền dịch vụ',
                                        renderMoney(currentItem.amount),
                                        { primary: true }
                                    )}
                                </Col>
                            </Row>

                            <div
                                style={{
                                    marginTop: 8,
                                    fontSize: 12,
                                    lineHeight: '18px',
                                    color: 'rgba(0, 0, 0, 0.45)',
                                }}
                            >
                                Thành tiền hiển thị theo giá trị dịch vụ đã được hệ thống ghi nhận.
                            </div>
                        </Card>

                        {/* NHÂN SỰ PHỤ TRÁCH */}
                        <Card
                            bordered={false}
                            style={pageCardStyle}
                            bodyStyle={{
                                padding: isMobile ? 12 : '12px 16px',
                            }}
                        >
                            {this.renderSectionTitle(
                                'Nhân sự phụ trách',
                                'Nhân sự đang được ghi nhận trên dịch vụ',
                                <CustomerServiceFailureReportForm
                                    customerService={currentItem}
                                    doctors={doctors}
                                    onReported={onStaffChanged}
                                />
                            )}

                            <Row gutter={[10, 10]} align="stretch">
                                <Col xs={24} md={12} xl={8}>
                                    {this.renderStaffGroup(
                                        'Nhân viên sales',
                                        currentItem?.sales,
                                        'sales'
                                    )}
                                </Col>

                                {doctorRoleGroups.map(group => (
                                    <Col xs={24} md={12} xl={8} key={group.key}>
                                        {this.renderDoctorRoleGroup(group)}
                                    </Col>
                                ))}

                                <Col xs={24} md={12} xl={8}>
                                    {this.renderStaffGroup(
                                        'Phụ tá',
                                        currentItem?.assistants,
                                        'assistant'
                                    )}
                                </Col>
                            </Row>
                        </Card>

                        {/* LỊCH SỬ CHUYỂN GIAO NHÂN SỰ */}
                        <CustomerServiceStaffHistory
                            customerServiceId={currentItem.id}
                            history={currentItem?.staff_assignment_changes}
                            roles={staffRoles}
                            components={staffComponents}
                            reasons={staffChangeReasons}
                            title="Lịch sử chuyển giao nhân sự"
                            variant="section"
                        />
                    </>
                ) : (
                    <Empty description="Không có dữ liệu dịch vụ" />
                )}
            </Drawer>
        );
    }
}

function mapStateToProps(state) {
    return {
        authUser: state.auth.authUser,
        staffRoles: state.config.customer_service_staff_roles || [],
        staffComponents:
            state.config.customer_service_staff_components || [],
        staffChangeReasons:
            state.config.customer_service_staff_change_reasons || [],
        doctors: state.config.doctors || [],
    };
}

export default connect(
    mapStateToProps,
    null
)(Form.create({ name: 'CustomerServicesDetail' })(CustomerServicesDetail));
